"use client";
import { useAskTessa } from "@cmp-src/tessa/AskTessaProvider";
import {
  GlobalSearchResourceItem,
  GlobalSearchUserResult,
  IDedicatedResourceRef,
  ISchedule,
  Office
} from "@gettactic/api";
import { MARIN_ROUTES } from "@gettactic/defines/src/web-routes";
import { buildUrl, createMapLink } from "@gettactic/helpers";
import { useOrgFeatureFlag } from "@gettactic/hooks";
import { format } from "date-fns";
import { useCallback } from "react";
import { useRouterPush } from "@/features/dashboard/helpers/router/useRouterPush";
import {
  MapModalParams,
  useMapModalContext
} from "@/features/map/components/modal";
import { useReserve } from "@/features/reserve/providers/context";
import { useRouter } from "@/lib/useRouter";
import { useGlobalSearch } from "../GlobalSearchProvider";

interface UseSearchNavigationParams {
  resources: GlobalSearchResourceItem[];
  offices: Office[];
  divisionCategories: any;
  currentOffice: any;
  onClose: () => void;
}

export function useSearchNavigation({
  resources,
  offices,
  divisionCategories,
  currentOffice,
  onClose
}: UseSearchNavigationParams) {
  const routerPush = useRouterPush();
  const router = useRouter();
  const { openReserveModal } = useReserve();
  const { openModal, closeModal } = useMapModalContext();

  const askTessa = useAskTessa();
  const {
    mapNavigator,
    mapControllers,
    startDate,
    endDate,
    timeMode,
    selectedOfficeId
  } = useGlobalSearch();
  const tessaFeatureFlag = useOrgFeatureFlag("tessa");

  const isOnMapPage = router.pathname === "/map";

  // Helper: Update map time filters
  const updateMapTimeFilters = useCallback(() => {
    if (!mapControllers) return;

    if (startDate) {
      mapControllers.changeStartDate(startDate);
    }
    if (endDate) {
      mapControllers.changeEndTime(endDate);
    }
    if (timeMode) {
      mapControllers.setTimeMode(timeMode);
    }
  }, [mapControllers, startDate, endDate, timeMode]);

  // Helper: Get effective dates with ISO string conversion
  const getEffectiveDates = useCallback(
    (useLocalTime = false) => {
      if (!startDate) return null;

      const effectiveStartDate = startDate;
      // We don't force +1 hour here anymore.
      // If endDate is missing, we let the MapProvider apply office defaults.
      // However, for the startTimeString/endTimeString we might still want an end if possible.
      const effectiveEndDate = endDate;

      const startTimeString = useLocalTime
        ? format(effectiveStartDate, "yyyy-MM-dd'T'HH:mm:ss")
        : effectiveStartDate.toISOString();

      const endTimeString = effectiveEndDate
        ? useLocalTime
          ? format(effectiveEndDate, "yyyy-MM-dd'T'HH:mm:ss")
          : effectiveEndDate.toISOString()
        : undefined;

      return {
        effectiveStartDate,
        effectiveEndDate,
        startTimeString,
        endTimeString
      };
    },
    [startDate, endDate]
  );

  // Helper: Handle map navigation or route push
  const handleMapNavigationOrPush = useCallback(
    (params: {
      officeId: string;
      resourceId?: string;
      floorId?: string;
      section?: MapModalParams["defaultTab"];
    }) => {
      const navigationDate = startDate;

      // Check if we're changing offices by comparing with current URL
      const currentOfficeId = new URLSearchParams(
        router.asPath.split("?")[1] || ""
      ).get("officeId");
      const isChangingOffice =
        currentOfficeId && currentOfficeId !== params.officeId;

      // Navigate on current page if already on map AND staying in same office
      if (isOnMapPage && mapControllers && !isChangingOffice) {
        updateMapTimeFilters();

        if (params.resourceId && mapNavigator) {
          // Update URL to include resource parameter for feature parity
          const dates = getEffectiveDates(false);
          const now = new Date();

          const mapLink = createMapLink({
            date:
              dates?.startTimeString || format(now, "yyyy-MM-dd'T'HH:mm:ss"),
            startTime: dates?.startTimeString,
            endTime: dates?.endTimeString,
            officeId: params.officeId,
            resourceId: params.resourceId,
            floorId: params.floorId,
            timeMode: timeMode || "manual",
            section: params.section
          });

          // Use router.replace to update URL without reload or new history entry
          router.replace(mapLink, undefined, { scroll: false });

          // Then navigate on the map
          mapNavigator({
            resourceId: params.resourceId,
            officeId: params.officeId,
            floorId: params.floorId,
            date: navigationDate || undefined,
            endDate: endDate || undefined,
            center: true
          });
        } else {
          mapControllers.navigateToOffice(
            params.officeId,
            navigationDate || undefined,
            timeMode || "manual",
            endDate || undefined
          );
        }
        onClose();
        return true;
      }

      return false;
    },
    [
      isOnMapPage,
      mapControllers,
      mapNavigator,
      startDate,
      endDate,
      timeMode,
      updateMapTimeFilters,
      onClose,
      router,
      getEffectiveDates
    ]
  );

  // Helper: Create and navigate to map link
  const navigateToMapLink = useCallback(
    (params: {
      officeId: string;
      resourceId?: string;
      floorId?: string;
      section?: MapModalParams["defaultTab"];
      useLocalTime?: boolean;
    }) => {
      const dates = getEffectiveDates(params.useLocalTime);
      const now = new Date();

      const mapLink = createMapLink({
        date: dates?.startTimeString || format(now, "yyyy-MM-dd'T'HH:mm:ss"),
        startTime: dates?.startTimeString,
        endTime: dates?.endTimeString,
        officeId: params.officeId,
        resourceId: params.resourceId,
        floorId: params.floorId,
        timeMode: timeMode || "manual",
        section: params.section
      });

      routerPush(mapLink);
      onClose();
    },
    [getEffectiveDates, timeMode, routerPush, onClose]
  );

  // Generic navigation handler
  const navigateToLocation = useCallback(
    (params: {
      officeId: string;
      resourceId?: string;
      floorId?: string;
      section?: "people" | "rooms" | "desks" | "other";
      useLocalTime?: boolean;
    }) => {
      // Try map navigation first if already on map page
      const navigatedOnMap = handleMapNavigationOrPush({
        officeId: params.officeId,
        resourceId: params.resourceId,
        floorId: params.floorId,
        section: params.section
      });

      if (navigatedOnMap) return;

      // Otherwise navigate to map page via URL
      navigateToMapLink({
        officeId: params.officeId,
        resourceId: params.resourceId,
        floorId: params.floorId,
        section: params.section,
        useLocalTime: params.useLocalTime ?? false
      });
    },
    [handleMapNavigationOrPush, navigateToMapLink]
  );

  const navigateToResource = useCallback(
    (resourceId: string) => {
      const resource = resources.find((r) => r.resource.id === resourceId);
      if (!resource) {
        onClose();
        return;
      }

      const officeId = resource.resource.office_id;
      const floorId = resource.resource.parent_id || undefined;
      const office =
        officeId && divisionCategories
          ? divisionCategories.offices[officeId]
          : currentOffice;

      if (!office) {
        onClose();
        return;
      }

      navigateToLocation({
        officeId,
        resourceId: resource.resource.id,
        floorId,
        useLocalTime: false
      });
    },
    [resources, divisionCategories, currentOffice, navigateToLocation, onClose]
  );

  // Jump to a person's dedicated desk on the map — the desk usually
  // isn't part of the search results, so navigate by its ids directly.
  const navigateToDedicatedResource = useCallback(
    (resource: IDedicatedResourceRef) => {
      if (!resource.office_id) {
        onClose();
        return;
      }
      navigateToLocation({
        officeId: resource.office_id,
        resourceId: resource.id,
        floorId: resource.parent_id || undefined,
        useLocalTime: false
      });
    },
    [navigateToLocation, onClose]
  );

  const navigateToOffice = useCallback(
    (officeId: string) => {
      navigateToLocation({
        officeId,
        useLocalTime: false
      });
    },
    [navigateToLocation]
  );

  const reserveOffice = useCallback(
    (officeId: string) => {
      const office = offices.find((o) => o.id === officeId);
      if (!office) {
        onClose();
        return;
      }

      openReserveModal({
        resourceType: "desk",
        office: office
      });

      onClose();
    },
    [offices, openReserveModal, onClose]
  );

  const navigateToPerson = useCallback(
    (person: GlobalSearchUserResult, _schedules: ISchedule[]) => {
      if (!person) {
        onClose();
        return;
      }

      routerPush(
        buildUrl(MARIN_ROUTES.directory, undefined, {
          queryParams: { user: person.id, tab: "schedule" }
        })
      );
      onClose();
    },
    [routerPush, onClose]
  );

  const handleSettingAction = useCallback(
    (settingId: string) => {
      if (settingId === "s-reserve-desk") {
        // Only pass startDate if it's a valid date, convert to ISO string
        const validStartDate =
          startDate && !Number.isNaN(startDate.getTime())
            ? startDate.toISOString()
            : undefined;
        openModal({
          date: validStartDate?.split("T")[0],
          officeId: currentOffice?.id,
          reservationModalIsOpen: true,
          onSuccess: () => {
            closeModal();
          },
          defaultTab: "desks"
        });
      } else if (settingId === "s-reserve-room") {
        // Only pass startDate if it's a valid date, convert to ISO string
        const validStartDate =
          startDate && !Number.isNaN(startDate.getTime())
            ? startDate.toISOString()
            : undefined;
        openModal({
          date: validStartDate?.split("T")[0],
          officeId: currentOffice?.id,
          reservationModalIsOpen: true,
          onSuccess: () => {
            closeModal();
          },
          defaultTab: "rooms"
        });
      }
      onClose();
    },
    [
      currentOffice,
      openReserveModal,
      openModal,
      closeModal,
      startDate,
      onClose,
      getEffectiveDates
    ]
  );

  const navigateToSetting = useCallback(
    (url: string) => {
      routerPush(url);
      onClose();
    },
    [routerPush, onClose]
  );

  const askTessaWithContext = useCallback(
    (query: string) => {
      if (!askTessa || !query.trim() || !tessaFeatureFlag) return;

      let message = query;

      if (startDate) {
        const dateStr = format(startDate, "MMMM d, yyyy");
        message += `\n\nDate: ${dateStr}`;
      }

      const officeIdToUse =
        selectedOfficeId !== undefined ? selectedOfficeId : currentOffice?.id;

      if (officeIdToUse === null) {
        message += "";
      } else if (officeIdToUse) {
        const office =
          offices.find((o) => o.id === officeIdToUse) || currentOffice;
        if (office) {
          message += `\nOffice: ${office.name}`;
        }
      }

      askTessa.tessaDisclosure.open({ initialPrompt: message });
      askTessa.setSelectedChatId("new");
      askTessa.fullPageToolDisclosure.close();

      onClose();
    },
    [
      askTessa,
      tessaFeatureFlag,
      startDate,
      selectedOfficeId,
      currentOffice,
      offices,
      onClose
    ]
  );

  return {
    navigateToResource,
    navigateToDedicatedResource,
    navigateToOffice,
    reserveOffice,
    navigateToPerson,
    handleSettingAction,
    navigateToSetting,
    askTessaWithContext
  };
}
